import { TFunction } from "i18next";
import startCase from "lodash/startCase";

import {
  BOOKING_HISTORY_DRAWERS,
  CRUD_ROUTE_ENTITY_DRAWER_TYPES,
  DEFAULT_HISTORY_ACCENT,
  DRAWER_TYPE_IMPLICIT_ENTITY,
  DRAWER_TYPE_TITLE_I18N_KEY,
  DrawerProps,
  DrawerType,
  drawerUnboundOrExplicitMode,
  EditFormMode,
  ENTITY_PLAQUE_ACCENT,
  HOURS_IN_DAY,
  IUserActionHistoryEntry,
  MILLISECONDS_IN_SECOND,
  PRODUCTION_HISTORY_DRAWERS,
  PRODUCTION_HISTORY_ENTITIES,
  RIDE_HISTORY_DRAWERS,
  RIDE_HISTORY_ENTITIES,
  RydeUiUnit,
  SECONDS_IN_MINUTE,
  UserActionHistoryOrigin,
} from "@ryde/shared";
import {
  blueMui,
  greenMui,
  indigoMui,
  orangeMui,
  purpleMui,
  rydeColors,
} from "@ryde/theme";

/**
 * Numeric id for a standard entity edit/duplicate-from route segment.
 */
export const parseCrudRecordIdFromPath = (
  pathname: string,
): number | undefined => {
  const editMatch = pathname.match(/\/edit\/(\d+)(?:\/|$)/);
  if (editMatch) {
    return Number(editMatch[1]);
  }
  const addDupMatch = pathname.match(/\/add\/(\d+)(?:\/|$)/);
  if (addDupMatch) {
    return Number(addDupMatch[1]);
  }
  return undefined;
};

/**
 * Splits "Booking 586313" into label + trailing numeric id for styling.
 */
export const splitHistoryPlaqueTitle = (
  normalized: string,
): { label: string; tailNumber?: string } => {
  const m = normalized.match(/^(.+?)\s+(\d+)\s*$/);
  if (m) {
    return { label: m[1].trim(), tailNumber: m[2] };
  }
  return { label: normalized.trim() };
};

/** Normalizes legacy strings; strips "ID:" so display is "Entity 123". */
export const normalizeHistoryCardTitleForDisplay = (value: string): string => {
  const t = value.trim();
  const mSingular = t.match(/^(.+?)\s+Singular\s+(\S+)\s*$/i);
  if (mSingular) {
    return `${mSingular[1].trim()} ${mSingular[2]}`;
  }
  return t
    .replace(/\s+Singular\s+/gi, " ")
    .replace(/\s+ID:\s*/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const entityIdTitle = (entity: RydeUiUnit, id: number | string): string => {
  return `${startCase(String(entity))} ${id}`;
};

export const resolveRecordedDrawerTitle = (
  type: DrawerType,
  raw: Record<string, unknown>,
  _t: TFunction,
): string => {
  const title = raw.title;
  if (typeof title === "string" && title.trim()) {
    return normalizeHistoryCardTitleForDisplay(title.trim());
  }
  const entityRaw = raw.entity;
  const idRaw = raw.id;
  if (typeof entityRaw === "string" && entityRaw !== "") {
    const entity = entityRaw as RydeUiUnit;
    if (typeof idRaw === "number") {
      return entityIdTitle(entity, idRaw);
    }
    if (typeof idRaw === "string" && idRaw.trim() !== "") {
      return entityIdTitle(entity, idRaw.trim());
    }
  }
  const processTitle = raw.processTitle;
  if (typeof processTitle === "string" && processTitle.trim()) {
    return normalizeHistoryCardTitleForDisplay(processTitle.trim());
  }
  return String(type);
};

const entityFromPayload = (
  payload: Record<string, unknown> | undefined,
): RydeUiUnit | undefined => {
  const raw = payload?.entity;
  return typeof raw === "string" && raw !== ""
    ? (raw as RydeUiUnit)
    : undefined;
};

const resolveHistoryEntityFromDrawerProps = (
  drawerType: DrawerType | undefined,
  drawerPayload: Record<string, unknown> | undefined,
): RydeUiUnit | undefined => {
  const fromPayload = entityFromPayload(drawerPayload);
  if (fromPayload) {
    return fromPayload;
  }
  return drawerType !== undefined
    ? DRAWER_TYPE_IMPLICIT_ENTITY[drawerType]
    : undefined;
};

export const isHistoryEntryUnboundDrawerMode = (
  entry: IUserActionHistoryEntry,
): boolean => {
  if (entry.origin !== UserActionHistoryOrigin.Drawer || !entry.drawerType) {
    return false;
  }
  if (entry.drawerType === DrawerType.RideEditForm) {
    return false;
  }
  if (!CRUD_ROUTE_ENTITY_DRAWER_TYPES.has(entry.drawerType)) {
    return false;
  }
  const props = {
    type: entry.drawerType,
    ...(entry.drawerPayload ?? {}),
  } as DrawerProps<DrawerType>;
  return drawerUnboundOrExplicitMode(props) === EditFormMode.UnboundDrawer;
};

export const plaqueAccentForHistoryEntry = (
  entry: IUserActionHistoryEntry,
): string => {
  const dt = entry.drawerType;
  const resolved = resolveHistoryEntityFromDrawerProps(
    entry.drawerType,
    entry.drawerPayload,
  );

  if (dt && PRODUCTION_HISTORY_DRAWERS.has(dt)) {
    return purpleMui._400;
  }
  if (dt === DrawerType.StationRouteEditForm || resolved === "stationRoute") {
    return rydeColors.stationRouteHistoryAccent;
  }
  if (dt === DrawerType.CourseEditForm || resolved === "course") {
    return orangeMui._400;
  }
  if (resolved === "booking" || (dt && BOOKING_HISTORY_DRAWERS.has(dt))) {
    return blueMui._600;
  }
  if (resolved && PRODUCTION_HISTORY_ENTITIES.has(resolved)) {
    return purpleMui._400;
  }

  const mappedAccent = resolved ? ENTITY_PLAQUE_ACCENT[resolved] : undefined;
  if (mappedAccent && resolved && !RIDE_HISTORY_ENTITIES.has(resolved)) {
    return mappedAccent;
  }

  if (resolved && RIDE_HISTORY_ENTITIES.has(resolved)) {
    return greenMui.A700;
  }
  if (dt && RIDE_HISTORY_DRAWERS.has(dt)) {
    return greenMui.A700;
  }

  if (dt === DrawerType.Notifications) {
    return indigoMui._500;
  }

  return DEFAULT_HISTORY_ACCENT;
};

/**
 * Localized plaque label (uses `entity.single` from payload or drawer-type mapping; optional `userActionsHistory.drawerTitles.*`).
 */
export const formatHistoryPlaqueDisplay = (
  entry: IUserActionHistoryEntry,
  t: TFunction,
): { label: string; tailNumber?: string } => {
  const normalized = normalizeHistoryCardTitleForDisplay(entry.title);
  const { label, tailNumber } = splitHistoryPlaqueTitle(normalized);
  const uiUnit = resolveHistoryEntityFromDrawerProps(
    entry.drawerType,
    entry.drawerPayload,
  );

  let rawLabel: string;
  if (uiUnit && uiUnit !== "unknown") {
    rawLabel = t(`${uiUnit}.single`, { defaultValue: label });
  } else {
    const specialKey = entry.drawerType
      ? DRAWER_TYPE_TITLE_I18N_KEY[entry.drawerType]
      : undefined;
    if (specialKey) {
      rawLabel = t(specialKey, { defaultValue: label });
    } else if (entry.drawerType) {
      rawLabel = t(`userActionsHistory.drawerTitles.${entry.drawerType}`, {
        defaultValue: label,
      });
    } else {
      rawLabel = label;
    }
  }

  return {
    label: startCase(rawLabel),
    tailNumber,
  };
};

/**
 * Elapsed wall time from `recordedAtMs` to `nowMs`, e.g. `1d 2h 24m 25s` (zero units omitted).
 */
export const formatHistoryElapsedAgo = ({
  recordedAtMs,
  nowMs,
}: {
  recordedAtMs: number;
  nowMs: number;
}): string => {
  const secondsInHour = SECONDS_IN_MINUTE * SECONDS_IN_MINUTE;
  const secondsInDay = HOURS_IN_DAY * secondsInHour;
  const elapsedSec = Math.max(
    0,
    Math.floor((nowMs - recordedAtMs) / MILLISECONDS_IN_SECOND),
  );
  const days = Math.floor(elapsedSec / secondsInDay);
  let rem = elapsedSec % secondsInDay;
  const h = Math.floor(rem / secondsInHour);
  rem %= secondsInHour;
  const m = Math.floor(rem / SECONDS_IN_MINUTE);
  const s = rem % SECONDS_IN_MINUTE;

  const parts: string[] = [];
  if (days > 0) {
    parts.push(`${days}d`);
  }
  if (h > 0) {
    parts.push(`${h}h`);
  }
  if (m > 0) {
    parts.push(`${m}m`);
  }
  if (s > 0 || parts.length === 0) {
    parts.push(`${s}s`);
  }
  return parts.join(" ");
};
